import { useState, useEffect } from 'react';
import LegalNoticesService from '../components/user/common/services/LegalNoticesService';

export const useLegalNotices = (location, audience = 'all', options = {}) => {
    const [notices, setNotices] = useState([]); // Always start with empty array
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dismissedNotices, setDismissedNotices] = useState(new Set());

    const { autoTrackViews = true } = options;

    useEffect(() => {
        fetchNotices();
    }, [location, audience]);

    const fetchNotices = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await LegalNoticesService.getNotices(location, audience);
            
            if (response && response.status && response.data) {
                // SAFETY CHECK: Ensure data is an array
                const noticesArray = Array.isArray(response.data) ? response.data : [];
                setNotices(noticesArray);

                // Auto-track views - ONLY if we have a valid array
                if (autoTrackViews && noticesArray.length > 0) {
                    noticesArray.forEach(notice => {
                        if (notice && notice._id) {
                            LegalNoticesService.trackView(notice._id);
                        }
                    });
                }
            } else {
                // If response structure is invalid, set empty array
                setNotices([]);
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching legal notices:', err);
            setNotices([]); // Always set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const dismissNotice = async (noticeId) => {
        if (!noticeId) return;
        
        setDismissedNotices(prev => new Set([...prev, noticeId]));
        try {
            await LegalNoticesService.trackDismissal(noticeId);
        } catch (error) {
            console.error('Error tracking dismissal:', error);
        }
    };

    const acknowledgeNotice = async (noticeId, userId) => {
        if (!noticeId) return;
        
        try {
            await LegalNoticesService.recordAcknowledgment(noticeId, userId);
            dismissNotice(noticeId);
        } catch (error) {
            console.error('Error recording acknowledgment:', error);
        }
    };

    const clickNotice = async (noticeId) => {
        if (!noticeId) return;
        
        try {
            await LegalNoticesService.trackClick(noticeId);
        } catch (error) {
            console.error('Error tracking click:', error);
        }
    };

    // SAFETY CHECK: Ensure notices is always an array before filtering
    const safeNotices = Array.isArray(notices) ? notices : [];
    const filteredNotices = safeNotices.filter(notice => 
        notice && notice._id && !dismissedNotices.has(notice._id)
    );

    return {
        notices: filteredNotices,
        loading,
        error,
        dismissNotice,
        acknowledgeNotice,
        clickNotice,
        refreshNotices: fetchNotices
    };
};